<template>
  <div class="ranking-tabs">
    <!-- LOADING -->
    <div v-if="loading" class="loading-ranking">
      <b-spinner small></b-spinner>
      <span>Carregando ranking...</span>
    </div>

    <template v-else>
      <!-- FILTROS PRINCIPAIS -->
      <div class="ranking-filtros">
        <div class="periodo-field">
          <label for="periodo-ranking">Período</label>

          <vue-select
            id="periodo-ranking"
            v-model="periodoSelecionado"
            :options="periodos"
            :clearable="false"
            :searchable="false"
            :disabled="loading"
            label="label"
            placeholder="Selecione o período"
            @input="alterarPeriodo"
          />
        </div>

        <div class="previsto-realizado-field">
          <FiltroPrevistoRealizado
            v-model="tipoPrevistoRealizado"
            @input="alterarTipoResultado"
          />
        </div>
      </div>

      <!-- FILTRO POR TIPO -->
      <div class="tipos-filtro-wrap">
        <div class="tipos-filtro">
          <button
            v-for="tipo in tiposEquipe"
            :key="tipo.key"
            type="button"
            class="tipo-filtro-btn"
            :class="{ active: tipoSelecionado === tipo.key }"
            @click="selecionarTipo(tipo.key)"
          >
            {{ tipo.label }}
          </button>
        </div>
      </div>

      <v-app class="app-tabela">
        <div class="box ranking-box">
          <v-card class="tabela">
            <v-card-title>
              <v-text-field
                v-model="search"
                append-icon="mdi-magnify"
                label="Buscar por CNES, UBS, INE ou equipe"
                class="input-bottom"
                single-line
                hide-details
              />
            </v-card-title>

            <v-data-table
              :page="currentPage"
              :footer-props="{
                itemsPerPageText: '',
                'items-per-page-options': [10],
                'disable-items-per-page': true,
              }"
              :loading="loading"
              :headers="headers"
              :items="dadosDaTabela"
              :search="search"
              :single-expand="true"
              :expanded.sync="expanded"
              item-key="id"
              class="elevation-1"
              loading-text="Carregando, por favor espere"
              show-expand
              @pagination="getPagination"
              @item-expanded="onItemExpanded"
            >
              <template v-slot:item.colocacao="{ item }">
                <span
                  class="position-badge"
                  :class="getPositionClass(item.ordem)"
                >
                  <i
                    v-if="[1, 2, 3].includes(Number(item.ordem))"
                    class="icofont-trophy"
                  ></i>

                  {{ item.ordem }}º
                </span>
              </template>

              <template v-slot:item.nota="{ item }">
                {{ item.nota ?? '-' }}
              </template>

              <template v-slot:item.resultado="{ item }">
                {{ item.resultado ?? '-' }}
              </template>

              <template v-slot:item.classificacao="{ item }">
                {{ item.classificacao ?? '-' }}
                <span
                  v-b-tooltip.html.hover
                  :title="getTextoClassificacaoTooltip(item)"
                  class="icon-tooltip"
                >
                  <i class="icofont-info-circle"></i>
                </span>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length">
                  <div class="expanded-content">
                    <div class="table-responsive">
                      <table class="table table-interna">
                        <thead>
                          <tr>
                            <th>Indicador</th>
                            <th>Peso</th>
                            <th>Numerador</th>
                            <th>Denominador</th>
                            <th>Nota</th>
                            <th>Pontuação</th>
                            <th>Classificação</th>
                          </tr>
                        </thead>

                        <tbody>
                          <tr
                            v-for="(indicador, index) in item.dadosIndicadores"
                            :key="`${item.id}-${indicador.indicador}-${index}`"
                          >
                            <td>
                              {{ indicador.ds_indicador }}
                            </td>

                            <td>
                              {{ indicador.peso ?? '-' }}
                            </td>

                            <td>
                              {{
                                getNumeradorIndicador(indicador, item.indicador)
                              }}
                            </td>

                            <td>
                              {{
                                getDenominadorIndicador(
                                  indicador,
                                  item.indicador
                                )
                              }}
                            </td>

                            <td>
                              {{ indicador.nota ?? '-' }}
                            </td>

                            <td>
                              {{ indicador.resultado ?? '-' }}
                            </td>

                            <td>
                              <TextoClassificacaoIndicador
                                v-if="tipoPrevistoRealizado === 'previsto'"
                                :idIndicador="indicador.indicador"
                                :classificacao="indicador.classificacao"
                                :resultado="indicador.resultado"
                              />
                              <span v-else>
                                {{
                                  formatarClassificacao(indicador.classificacao)
                                }}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- <div v-if="item.dadosResumo" class="dados-resumo">
                      <div
                        v-for="resumo in item.dadosResumo"
                        :key="resumo.label"
                        class="resumo-item"
                      >
                        <span>{{ resumo.label }}</span>
                        <strong>{{ resumo.value }}</strong>
                      </div>
                    </div> -->
                  </div>
                </td>
              </template>

              <template v-slot:[`footer.page-text`]="items">
                <div class="info-table-item tabela-esus-pec">
                  <FonteSincronizadorC />
                </div>

                <span class="paginacao">
                  {{ items.pageStart }} - {{ items.pageStop }} de
                  {{ items.itemsLength }} registros
                </span>
              </template>

              <template v-slot:progress>
                <v-progress-linear color="blue" :height="10" indeterminate />
              </template>

              <template v-slot:no-data>
                <div class="alert alert-info mb-0">
                  <b>
                    Nenhuma informação encontrada. Selecione outro filtro.
                  </b>
                </div>
              </template>

              <template v-slot:no-results>
                <div class="alert alert-info mb-0">
                  <b> Nenhuma informação encontrada para a busca realizada. </b>
                </div>
              </template>
            </v-data-table>
          </v-card>
        </div>
      </v-app>
    </template>
  </div>
</template>

<script>
import FiltroPrevistoRealizado from '@/pages/IndicadoresAps/ResultadoMunicipal/components/FiltroPrevistoRealizado.vue'
import TextoClassificacaoIndicador from '@/components/utils/TextoClassificacaoIndicador'
import utils from '@/mixins/utils.js'

export default {
  name: 'Ranking',
  components: {
    FiltroPrevistoRealizado,
    TextoClassificacaoIndicador,
  },
  mixins: [utils],
  data() {
    return {
      loading: false,
      search: '',
      currentPage: 1,
      expanded: [],
      periodoSelecionado: null,
      periodos: [],
      tipoPrevistoRealizado: 'previsto',
      dadosRankingRaw: [],
      dadosDaTabela: [],
      tiposEquipe: [
        {
          key: 'CVAT',
          label: 'CVAT',
        },
        {
          key: 'eSF',
          label: 'eSF',
        },
        {
          key: 'eAP',
          label: 'eAP',
        },
        {
          key: 'eSB',
          label: 'eSB',
        },
        {
          key: 'eMulti',
          label: 'eMulti',
        },
      ],
      tipoSelecionado: 'eSF',
      headers: [
        {
          text: 'N°',
          align: 'center',
          sortable: true,
          filterable: false,
          value: 'colocacao',
          width: '90px',
        },
        {
          text: 'CNES',
          align: 'start',
          filterable: true,
          value: 'nu_cnes',
          width: '130px',
        },
        {
          text: 'UBS',
          align: 'start',
          filterable: true,
          value: 'no_unidade_saude',
        },
        {
          text: 'INE',
          align: 'start',
          filterable: true,
          value: 'nu_ine',
          width: '150px',
        },
        {
          text: 'Equipe',
          align: 'start',
          filterable: true,
          value: 'no_equipe',
        },
        {
          text: 'Nota',
          align: 'start',
          filterable: false,
          value: 'nota',
        },
        {
          text: 'Pontuação',
          align: 'start',
          filterable: false,
          value: 'resultado',
        },
        {
          text: 'Classificação',
          align: 'start',
          filterable: false,
          value: 'classificacao',
        },
        {
          text: '',
          value: 'data-table-expand',
        },
      ],
    }
  },

  computed: {
    /**
     * Retorna o texto correspondente ao tipo de resultado selecionado.
     *
     * @return {String}
     */
    labelTipoResultado() {
      return this.tipoPrevistoRealizado === 'realizado'
        ? 'Realizado'
        : 'Previsto'
    },
  },

  mounted() {
    this.carregarRanking()
  },

  methods: {
    /**
     * Retorna a classe visual correspondente à posição no ranking.
     *
     * @param {Number|String} posicao Posição da equipe.
     * @return {String}
     */
    getPositionClass(posicao) {
      const posicaoNormalizada = Number(posicao)

      if (posicaoNormalizada === 1) {
        return 'first'
      }

      if (posicaoNormalizada === 2) {
        return 'second'
      }

      if (posicaoNormalizada === 3) {
        return 'third'
      }

      return ''
    },

    /**
     * Retorna o texto do tooltip da classificação da equipe.
     *
     * @param {Object} item Dados da equipe.
     * @returns {String|null} Texto do tooltip.
     */
    getTextoClassificacaoTooltip(item) {
      const lista = this.$store.state.regrasIndicadoresAps || []

      const classificacao = String(item.classificacao || '').toUpperCase()

      const itens =
        classificacao === 'REGULAR'
          ? lista.filter(
              (i) =>
                i.id_indicador == item.indicador &&
                String(i.classificacao || '').toUpperCase() === classificacao
            )
          : [
              lista.find(
                (i) =>
                  i.id_indicador == item.indicador &&
                  String(i.classificacao || '').toUpperCase() === classificacao
              ),
            ].filter(Boolean)

      if (!itens.length) return null

      const partesParametro = itens
        .map((item) => {
          const partes = []

          if (item.sinal_min && item.min_val) {
            partes.push(`${item.sinal_min} ${item.min_val}`)
          }

          if (item.sinal_max && item.max_val) {
            partes.push(`${item.sinal_max} ${item.max_val}`)
          }

          // Se vierem os dois no mesmo registro, junta com " e "
          return partes.join(' e ')
        })
        .filter(Boolean)

      const parametro = partesParametro.join(' e ')

      const ref = itens[0]

      return `Peso: ${ref.peso} <br>
              Nota: ${item.nota} ${parametro} <br>
              Classificação: ${ref.classificacao} `
    },

    /**
     * Carrega os dados completos do ranking.
     *
     * Após o carregamento inicial, as alterações de período, tipo de equipe
     * e previsto/realizado são realizadas apenas no front.
     *
     * @return {Promise<void>}
     */
    async carregarRanking() {
      try {
        this.loading = true
        this.resetarDados()

        const url =
          `/app/indicadores-aps/resultado-indicadores/` +
          `${this.$store.state.cod_ibge}/distribuicao-equipes-ranking`

        const response = await this.$api.get(url)

        this.dadosRankingRaw = Array.isArray(response?.data)
          ? response.data
          : []

        this.prepararPeriodos()
        this.aplicarFiltros()
      } catch (error) {
        console.error('Erro ao carregar o ranking:', error)
        this.resetarDados()
      } finally {
        this.loading = false
      }
    },

    /**
     * Limpa os dados utilizados pelo componente.
     *
     * @return {void}
     */
    resetarDados() {
      this.search = ''
      this.currentPage = 1
      this.expanded = []
      this.periodoSelecionado = null
      this.periodos = []
      this.dadosRankingRaw = []
      this.dadosDaTabela = []
    },

    /**
     * Monta as opções de período conforme o tipo de resultado selecionado.
     *
     * Para resultados realizados, disponibiliza somente os meses finais
     * dos quadrimestres: abril, agosto e dezembro.
     *
     * Mantém o período anteriormente selecionado quando ele continuar
     * disponível. Caso contrário, seleciona o período mais recente.
     *
     * @return {void}
     */
    prepararPeriodos() {
      const periodoAnterior = this.periodoSelecionado?.value
      const mesesRealizados = [4, 8, 12]
      const periodosUnicos = new Map()

      this.dadosRankingRaw.forEach((item) => {
        const mesAno = String(item.mes_ano || '').trim()
        const mesAnoQuad = String(item.mes_ano_quad || mesAno).trim()

        if (!mesAno) {
          return
        }

        const partes = mesAno.split('/')

        if (partes.length !== 2) {
          return
        }

        const mes = Number(partes[0])
        const ano = Number(partes[1])

        if (
          !Number.isInteger(mes) ||
          !Number.isInteger(ano) ||
          mes < 1 ||
          mes > 12
        ) {
          return
        }

        if (
          this.tipoPrevistoRealizado === 'realizado' &&
          !mesesRealizados.includes(mes)
        ) {
          return
        }

        if (!periodosUnicos.has(mesAno)) {
          periodosUnicos.set(mesAno, {
            value: mesAno,
            label: mesAnoQuad,
            mes,
            ano,
          })
        }
      })

      this.periodos = Array.from(periodosUnicos.values()).sort((a, b) => {
        if (a.ano !== b.ano) {
          return b.ano - a.ano
        }

        return b.mes - a.mes
      })

      const periodoExistente = this.periodos.find(
        (periodo) => periodo.value === periodoAnterior
      )

      this.periodoSelecionado = periodoExistente || this.periodos[0] || null
    },

    /**
     * Atualiza o ranking conforme o período selecionado.
     *
     * @param {Object|null} periodo Período selecionado.
     * @return {void}
     */
    alterarPeriodo(periodo) {
      this.periodoSelecionado = periodo
      this.fecharExpansao()
      this.aplicarFiltros()
    },

    /**
     * Altera a origem dos valores exibidos entre previsto e realizado.
     *
     * Ao selecionar realizado, o filtro de período passa a disponibilizar
     * somente os meses 4, 8 e 12 e seleciona o mais recente.
     *
     * @param {String} tipo Tipo selecionado.
     * @return {void}
     */
    alterarTipoResultado(tipo) {
      this.tipoPrevistoRealizado =
        tipo === 'realizado' ? 'realizado' : 'previsto'

      this.fecharExpansao()
      this.prepararPeriodos()
      this.aplicarFiltros()
    },

    /**
     * Alterna a seleção de um tipo de equipe.
     *
     * Mantém sempre ao menos um tipo selecionado.
     *
     * @param {String} tipo Tipo selecionado.
     * @return {void}
     */
    alternarTipo(tipo) {
      const index = this.tiposSelecionados.indexOf(tipo)

      if (index === -1) {
        this.tiposSelecionados.push(tipo)
      } else if (this.tiposSelecionados.length > 1) {
        this.tiposSelecionados.splice(index, 1)
      }

      this.fecharExpansao()
      this.aplicarFiltros()
    },

    /**
     * Seleciona o tipo de equipe utilizado no ranking.
     *
     * Apenas um tipo pode permanecer selecionado por vez.
     *
     * @param {String} tipo Tipo selecionado.
     * @return {void}
     */
    selecionarTipo(tipo) {
      if (this.tipoSelecionado === tipo) {
        return
      }

      this.tipoSelecionado = tipo
      this.fecharExpansao()
      this.aplicarFiltros()
    },

    /**
     * Aplica os filtros selecionados e monta os registros da tabela.
     *
     * @return {void}
     */
    aplicarFiltros() {
      const mesAno = this.periodoSelecionado?.value

      if (!mesAno) {
        this.dadosDaTabela = []
        return
      }

      const dadosFiltrados = this.dadosRankingRaw.filter((item) => {
        const mesmoPeriodo =
          String(item.mes_ano || '').trim() === String(mesAno).trim()

        const tipo = this.getTipoRegistro(item)

        return mesmoPeriodo && tipo === this.tipoSelecionado
      })

      this.dadosDaTabela = this.mapearRanking(dadosFiltrados)
    },

    /**
     * Mapeia os dados da API para o formato utilizado pela tabela.
     *
     * @param {Array} dados Dados retornados pela API.
     * @return {Array}
     */
    mapearRanking(dados) {
      return dados
        .map((item, index) => {
          const dadosEquipe = this.parseObjectJson(item.dados_equipes)
          const dadosEfb = this.parseObjectJson(item.dados_efb)
          const dadosMs = this.parseObjectJson(item.dados_ms)
          const tipo = this.getTipoRegistro(item)
          const realizado = this.tipoPrevistoRealizado === 'realizado'
          const ordem = realizado
            ? Number(item.ordem_resultado_realizado)
            : Number(item.ordem_resultado_previsto)
          const nota = realizado ? item.nota_realizada : item.nota_prevista
          const resultado = realizado
            ? item.resultado_realizado
            : item.resultado_previsto
          const classificacao = realizado
            ? item.classificacao_realizada
            : item.classificacao_prevista
          const dadosResultado = realizado ? dadosMs : dadosEfb

          return {
            ...item,

            id: this.montarIdItem(item, dadosEquipe, index),
            ordem: Number.isFinite(ordem) && ordem > 0 ? ordem : 999999,
            colocacao: Number.isFinite(ordem) ? ordem : 999999,
            tipo,
            nu_cnes: dadosEquipe.nu_cnes || '-',
            no_unidade_saude: dadosEquipe.no_unidade_saude || '-',
            nu_ine: dadosEquipe.nu_ine || '-',
            no_equipe: dadosEquipe.no_equipe || '-',
            nota: this.normalizarValor(nota),
            resultado: this.normalizarValor(resultado),
            classificacao: classificacao || '-',
            dadosEquipe,
            dadosResultado,
            dadosIndicadores: this.getIndicadoresInternos(
              dadosResultado,
              item.indicador
            ),
            dadosResumo: this.getDadosResumo(dadosResultado, item.indicador),
          }
        })
        .sort((a, b) => {
          if (a.ordem !== b.ordem) {
            return a.ordem - b.ordem
          }

          return String(a.no_equipe).localeCompare(String(b.no_equipe), 'pt-BR')
        })
    },

    /**
     * Retorna o tipo do registro conforme o indicador final
     * e o tipo da equipe.
     *
     * Para o indicador 57, diferencia eSF e eAP utilizando
     * o campo sg_tipo_equipe retornado em dados_equipes.
     *
     * @param {Object} item Registro da API.
     * @return {String}
     */
    getTipoRegistro(item) {
      const indicador = Number(item.indicador)

      if (indicador === 57) {
        const dadosEquipe = this.parseObjectJson(item.dados_equipes)
        const tipoEquipe = String(
          dadosEquipe.sg_tipo_equipe || ''
        ).toUpperCase()

        if (tipoEquipe === 'EAP') {
          return 'eAP'
        }

        return 'eSF'
      }

      const tiposPorIndicador = {
        56: 'CVAT',
        58: 'eSB',
        59: 'eMulti',
      }

      return tiposPorIndicador[indicador] || 'CVAT'
    },

    /**
     * Retorna os indicadores internos do resultado selecionado.
     *
     * @param {Object} dadosResultado Dados previstos ou realizados.
     * @param {Number|String} indicadorFinal Indicador final.
     * @return {Array}
     */
    getIndicadoresInternos(dadosResultado, indicadorFinal) {
      if (dadosResultado && Array.isArray(dadosResultado.all_indicadores)) {
        return dadosResultado.all_indicadores
      }

      /*
       * O CVAT previsto pode retornar apenas os campos referentes às
       * dimensões 54 e 55, sem o array all_indicadores.
       */
      if (Number(indicadorFinal) === 56 && dadosResultado) {
        const indicadores = []

        const resultadoCadastro = dadosResultado.escore_param_cad
        const resultadoAcompanhamento = dadosResultado.escore_param_acomp

        if (resultadoCadastro !== null && resultadoCadastro !== undefined) {
          indicadores.push({
            indicador: 54,
            ds_indicador: 'X - Classificação por parâmetro de cadastro',
            resultado: '-',
            nota: resultadoCadastro,
            peso: '3',
            numerador: '-',
            denominador: '-',
            classificacao: '-',
          })
        }

        if (
          resultadoAcompanhamento !== null &&
          resultadoAcompanhamento !== undefined
        ) {
          indicadores.push({
            indicador: 55,
            ds_indicador: 'Y - Acompanhamento por parâmetro de cadastro',
            resultado: '-',
            nota: resultadoAcompanhamento,
            peso: '7',
            numerador: '-',
            denominador: '-',
            classificacao: '-',
          })
        }

        return indicadores
      }

      return []
    },

    /**
     * Monta informações complementares do CVAT previsto.
     *
     * @param {Object} dadosResultado Dados previstos ou realizados.
     * @param {Number|String} indicadorFinal Indicador final.
     * @return {Array}
     */
    getDadosResumo(dadosResultado, indicadorFinal) {
      if (
        Number(indicadorFinal) !== 56 ||
        !dadosResultado ||
        Array.isArray(dadosResultado.all_indicadores)
      ) {
        return []
      }

      const campos = [
        {
          label: 'Cadastros no PEC',
          value: dadosResultado.cad_pec,
        },
        {
          label: 'Parâmetro de cadastro',
          value: dadosResultado.cad_param,
        },
        {
          label: 'Numerador',
          value: dadosResultado.numerador,
        },
        {
          label: 'Cadastros acompanhados',
          value: dadosResultado.qtd_cad_acomp,
        },
      ]

      return campos
        .filter(
          (item) =>
            item.value !== null && item.value !== undefined && item.value !== ''
        )
        .map((item) => ({
          ...item,
        }))
    },

    /**
     * Retorna o numerador do indicador.
     *
     * Para os indicadores 54 e 55 e para resultados realizados, o campo
     * não é exibido.
     *
     * @param {Object} indicador Dados do indicador.
     * @param {Number|String} indicadorFinal Indicador final.
     * @return {*}
     */
    getNumeradorIndicador(indicador, indicadorFinal) {
      if (
        this.tipoPrevistoRealizado === 'realizado' ||
        [54, 55].includes(Number(indicador.indicador)) ||
        Number(indicadorFinal) === 56
      ) {
        return '-'
      }

      return indicador.numerador ?? '-'
    },

    /**
     * Retorna o denominador do indicador.
     *
     * Para os indicadores 54 e 55 e para resultados realizados, o campo
     * não é exibido.
     *
     * O indicador 47 mantém seu denominador quando retornado pela API.
     *
     * @param {Object} indicador Dados do indicador.
     * @param {Number|String} indicadorFinal Indicador final.
     * @return {*}
     */
    getDenominadorIndicador(indicador, indicadorFinal) {
      if (
        this.tipoPrevistoRealizado === 'realizado' ||
        [54, 55].includes(Number(indicador.indicador)) ||
        Number(indicadorFinal) === 56
      ) {
        return '-'
      }

      return indicador.denominador ?? '-'
    },

    /**
     * Monta uma chave única para o registro da tabela.
     *
     * @param {Object} item Registro original.
     * @param {Object} dadosEquipe Dados da equipe.
     * @param {Number} index Índice original.
     * @return {String}
     */
    montarIdItem(item, dadosEquipe, index) {
      return [
        item.mes_ano || '',
        item.indicador || '',
        dadosEquipe.nu_ine || '',
        dadosEquipe.nu_cnes || '',
        index,
      ].join('-')
    },

    /**
     * Normaliza valores numéricos sem transformar nulo em zero.
     *
     * @param {*} valor Valor recebido.
     * @return {*}
     */
    normalizarValor(valor) {
      if (valor === null || valor === undefined || valor === '') {
        return '-'
      }

      const numero = Number(valor)

      return Number.isFinite(numero) ? numero : valor
    },

    /**
     * Padroniza a exibição das classificações.
     *
     * @param {String} classificacao Classificação recebida.
     * @return {String}
     */
    formatarClassificacao(classificacao) {
      const valor = String(classificacao || '')
        .trim()
        .toUpperCase()

      const classificacoes = {
        ÓTIMO: 'Ótimo',
        OTIMO: 'Ótimo',
        BOM: 'Bom',
        SUFICIENTE: 'Suficiente',
        REGULAR: 'Regular',
      }

      return classificacoes[valor] || classificacao || '-'
    },

    /**
     * Fecha o registro expandido e retorna para a primeira página.
     *
     * @return {void}
     */
    fecharExpansao() {
      this.expanded = []
      this.currentPage = 1
    },

    /**
     * Atualiza a página atual da tabela.
     *
     * @param {Object} options Informações da paginação.
     * @return {void}
     */
    getPagination(options) {
      this.currentPage = options.page
    },

    /**
     * Manipula a expansão de um registro.
     *
     * @param {Object} event Dados do evento.
     * @return {void}
     */
    onItemExpanded(event) {
      if (!event?.value) {
        return
      }

      this.expanded = [event.item]
    },
  },
}
</script>

<style scoped lang="scss">
.ranking-tabs {
  width: 100%;
  margin-top: 28px;
  animation: fadeInUp 0.8s ease;

  .ranking-filtros {
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 24px;
    flex-wrap: wrap;
    margin-bottom: 18px;

    .periodo-field {
      width: 240px;

      label {
        display: block;
        margin-bottom: 6px;
        color: #475569;
        font-size: 12px;
        font-weight: 800;
        letter-spacing: 0.5px;
      }
    }

    .previsto-realizado-field {
      min-height: 40px;
      display: flex;
      align-items: center;
      padding-bottom: 2px;
    }

    @media (max-width: 575px) {
      align-items: stretch;

      .periodo-field {
        width: 100%;
      }

      .previsto-realizado-field {
        width: 100%;
        min-height: auto;
        padding-bottom: 0;
      }
    }
  }

  .tipos-filtro-wrap {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
    margin-bottom: 24px;
    padding: 14px 16px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
  }

  .tipos-filtro {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .tipo-filtro-btn {
    border: 1px solid #cbd5e1;
    background: #ffffff;
    color: #475569;
    border-radius: 999px;
    padding: 7px 14px;
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      border-color: #4f46e5;
      color: #4f46e5;
    }

    &.active {
      background: #4f46e5;
      border-color: #4f46e5;
      color: #ffffff;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.45;
      background: #f1f5f9;
      border-color: #cbd5e1;
      color: #94a3b8;
    }

    &:disabled:hover {
      border-color: #cbd5e1;
      color: #94a3b8;
    }
  }

  .resultado-atual {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #334155;
    font-size: 13px;
  }

  .resultado-atual-label {
    color: #64748b;
  }

  .loading-ranking {
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #64748b;
    font-weight: 700;
  }

  .ranking-box {
    margin-top: 0;
  }

  .app-tabela {
    min-height: auto;
    background: transparent !important;
  }

  .position-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    color: #000;
    font-weight: 900;
    white-space: nowrap;

    i {
      font-size: 13px;
    }

    &.first,
    &.second,
    &.third {
      padding: 4px 8px;
      color: #ffffff;
      border-radius: 999px;
    }

    &.first {
      background: #facc15;
    }

    &.second {
      background: #c2cad6;
    }

    &.third {
      background: #b45309;
    }
  }

  .expanded-label {
    color: #64748b;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .table-interna {
    margin-bottom: 0;
    background: #ffffff;

    th {
      border-top: 0;
      background: #f4f6f8;
      color: #374151;
      font-size: 12px;
      white-space: nowrap;
    }

    td {
      vertical-align: middle;
      color: #374151;
      font-size: 12px;
    }
  }

  .empty-internal {
    padding: 25px !important;
    color: #64748b;
    text-align: center;
  }

  .dados-resumo {
    display: grid;
    grid-template-columns: repeat(4, minmax(130px, 1fr));
    gap: 12px;
    margin-top: 18px;
  }

  .resumo-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px 14px;
    border: 1px solid #e5e9ef;
    border-radius: 8px;
    background: #ffffff;

    span {
      color: #64748b;
      font-size: 11px;
      font-weight: 700;
    }

    strong {
      color: #1f2937;
      font-size: 15px;
    }
  }

  @media screen and (max-width: 900px) {
    .tipos-filtro-wrap {
      align-items: flex-start;
      flex-direction: column;
    }

    .dados-resumo {
      grid-template-columns: repeat(2, minmax(130px, 1fr));
    }
  }

  @media screen and (max-width: 600px) {
    .tipos-filtro-wrap {
      padding: 12px;
    }

    .tipos-filtro {
      width: 100%;
    }

    .tipo-filtro-btn {
      flex: 1;
      min-width: calc(50% - 4px);
      text-align: center;
    }

    .dados-resumo {
      grid-template-columns: 1fr;
    }
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
